import { useState, useEffect } from 'react';
import { collection, getDocs, query, where, Timestamp } from 'firebase/firestore';
import { db } from '../lib/firebase/config';

interface AdminStats {
  totalUsers: number;
  activeUsers: number;
  totalSolves: number;
  averageTime: number;
}

export const useAdminStats = () => {
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        
        // Get all users
        const usersRef = collection(db, 'users');
        const usersSnap = await getDocs(usersRef);
        const totalUsers = usersSnap.size;

        // Calculate active users (logged in within last 30 days)
        const thirtyDaysAgo = Timestamp.fromDate(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000));
        const activeUsersQuery = query(
          usersRef,
          where('lastLogin', '>=', thirtyDaysAgo)
        );
        const activeUsersSnap = await getDocs(activeUsersQuery);
        const activeUsers = activeUsersSnap.size;

        // Get all solves from top-level collection
        const solvesRef = collection(db, 'solves');
        const solvesSnap = await getDocs(solvesRef);
        const solves = solvesSnap.docs.map(doc => doc.data());
        
        // Calculate total solves and average time
        const totalSolves = solves.length;
        const validSolves = solves.filter(solve => 
          typeof solve.time === 'number' && 
          solve.time > 0 && 
          !solve.dnf
        );
        const totalTime = validSolves.reduce((acc, solve) => acc + solve.time, 0);
        // Average time is already in milliseconds, which is what formatTime expects
        const averageTime = validSolves.length > 0 ? totalTime / validSolves.length : 0;

        setStats({
          totalUsers,
          activeUsers,
          totalSolves,
          averageTime
        });
      } catch (err) {
        console.error('Error fetching admin stats:', err);
        setError(err instanceof Error ? err : new Error('Unknown error'));
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  return { stats, loading, error };
}; 